import React from 'react';
import { useThrottle } from '@wix/da-shared-react/pkg/utils/hooks/useThrottle';
import { useTranslation } from 'react-i18next';
import {
  default as OnScrollBottom,
  DISTANCE_UNIT,
} from '@wix/da-shared-react/pkg/OnScrollBottom';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import Pagination from '@wix/da-shared-react/pkg/Pagination';
import s from './PagingWrapper.scss';

const DISTANCE_FROM_BOTTOM_TO_FETCH_MORE = 1000;
const FETCH_MORE_THROTTLE = 300;

export interface Props {
  streamId: string;
  isInfiniteScroll: boolean;
  isDuperbrowseActive: boolean;
  hasMore: boolean;
  hasLess: boolean;
  isFetching: boolean;
  nextPageUrl: string;
  prevPageUrl: string;
  pageType?: string;
  error?: string;
  customEndOfResults?: React.ReactNode;

  fetchMore: (streamId: string) => void;
  children?: React.ReactNode;
}

const PagingWrapper: React.FC<Props> = ({
  streamId,
  children,
  isFetching,
  hasMore,
  hasLess,
  isDuperbrowseActive,
  isInfiniteScroll,
  nextPageUrl,
  prevPageUrl,
  pageType,
  error,
  customEndOfResults,
  fetchMore,
}) => {
  const { t } = useTranslation();
  const handleScrollToBottom = useThrottle(
    () => {
      if (isFetching || !hasMore || isDuperbrowseActive || error) {
        return;
      }

      fetchMore(streamId);
    },
    [fetchMore, hasMore, isDuperbrowseActive, isFetching, error, streamId],
    FETCH_MORE_THROTTLE
  );

  const currentPage = hasLess ? 2 : 1;
  const totalPages = hasMore ? currentPage + 1 : currentPage;

  return (
    <>
      {children}
      {isFetching && <LoadingIndicator />}
      {!isFetching &&
        !hasMore &&
        (customEndOfResults ?? (
          <div className={s['end-of-results']}>
            {t([`facetPage.${pageType}.endResults`, 'facetPage.endResults'])}
          </div>
        ))}
      {!isInfiniteScroll && (
        <div className={s['paged']}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            showPageNumbers={false}
            getPageUrl={page =>
              page > currentPage ? nextPageUrl : prevPageUrl
            }
          />
        </div>
      )}
      {isInfiniteScroll && (
        <OnScrollBottom
          checkOnMount
          checkOnUpdate={hasMore}
          onScrolledToBottom={handleScrollToBottom}
          distanceToBottom={DISTANCE_FROM_BOTTOM_TO_FETCH_MORE}
          distanceUnit={DISTANCE_UNIT.PX}
        />
      )}
    </>
  );
};
PagingWrapper.displayName = 'PagingWrapper';

export default PagingWrapper;
